import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';

import {
  DirectTradingAccountSecurityFundHoldingsDocument,
  DirectTradingAccountSecurityFundHoldingsQuery,
} from './graphql/directTradingAccountSecurityFundHoldings.generated';

import { NarrowTypename } from '../../../graphql/utils/narrow-typename';
import { ThemedImage } from '../../../Shared/components-v2/themed-image/ThemedImage';
import { iconNameForSymbol, weightDisplayFor } from '../../utils';

type FinancialSecurity = NarrowTypename<
  DirectTradingAccountSecurityFundHoldingsQuery['account'],
  'DirectTradingAccount'
>['financialSecurity'];

type Holdings = NarrowTypename<FinancialSecurity, 'FinancialSecurityFund'>['portfolio']['holdings'];
type Holding = Holdings[number];

type HoldingSecurity = NarrowTypename<Holding, 'FinancialSecurityFundPortfolioHolding'>['security'];

const iconDisplayForSecurity = (security: HoldingSecurity, symbol: string | null | undefined) => {
  const defaultIconElement = <Icon color="icon" name={iconNameForSymbol(symbol)} size="small" />;
  let iconElement = defaultIconElement;

  if (security && security.__typename === 'FinancialSecurityStock') {
    const { companyProfile } = security;
    const { logo } = companyProfile;

    if (logo) {
      iconElement = <ThemedImage alt="logo" themedAsset={logo} />;
    }
  }

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'size.450',
        height: 'size.450',
        overflow: 'hidden',
      }}
    >
      {iconElement}
    </Box>
  );
};

export const isDisplayableFundHolding = (fundHolding: Holding) => {
  return (
    fundHolding.weight > 0 &&
    !(fundHolding.symbol === null && fundHolding.name.toLowerCase() === 'equity other' && fundHolding.weight === 100)
  );
};

export const displayableFundHoldings = (fundHoldings: Holdings) => {
  return fundHoldings.filter(fundHolding => isDisplayableFundHolding(fundHolding));
};

type HoldingRowProps = {
  holding: Holding;
};

const HoldingRow: React.FC<HoldingRowProps> = ({ holding }) => {
  const security: HoldingSecurity = holding?.security;

  return (
    <ListRow
      css={{
        padding: 'space.0',
        paddingY: 'space.200',
        borderBottom: '1px solid token(colors.border.weak)',
        '&:first-child': { paddingTop: 'space.0' },
        '&:last-child': { borderBottom: 'none', paddingBottom: 0 },
      }}
    >
      <ListRow.LeadingAsset>{iconDisplayForSecurity(security, holding.symbol)}</ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text variant="text100">{holding.name}</Text>
        <Text css={{ color: 'text.weak' }} variant="text50">
          {holding.symbol}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Text>{weightDisplayFor(holding.weight)}%</Text>
      </ListRow.TrailingContent>
    </ListRow>
  );
};

type Props = {
  accountId: string;
  securityId: string;
};

export const HoldingsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityFundHoldingsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    return null;
  }

  const { account } = data;

  if (!account.financialSecurity || account.financialSecurity.__typename !== 'FinancialSecurityFund') {
    return null;
  }

  const { financialSecurity } = account;
  const { portfolio } = financialSecurity;
  const { holdings } = portfolio;

  const filteredHoldings = displayableFundHoldings(holdings);

  if (filteredHoldings.length === 0) {
    return null;
  }

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <Heading as="h2" variant="title200">
          Top holdings
        </Heading>
      </Card.Header>
      <Separator />
      <Card.Content>
        {filteredHoldings.map(holding => (
          <HoldingRow key={holding.symbol} holding={holding} />
        ))}
      </Card.Content>
    </Card>
  );
};
